/**
 * Parse an estimate string into a number of minutes.
 *
 * Accepted forms (case-insensitive): "30m", "2h", "1h30m", "45" (bare integer = minutes).
 * Returns undefined for anything that doesn't cleanly parse, or for zero.
 */
export function parseEstimate(input: string): number | undefined {
  if (!input) return undefined;
  const s = input.trim().toLowerCase();
  if (!s) return undefined;

  // Bare integer = minutes.
  if (/^\d+$/.test(s)) {
    const n = Number.parseInt(s, 10);
    return n > 0 ? n : undefined;
  }

  // Hours and/or minutes (integer values only — no fractional hours).
  const match = /^(?:(\d+)h)?(?:(\d+)m)?$/.exec(s);
  if (!match) return undefined;
  const hours = match[1] ? Number.parseInt(match[1], 10) : 0;
  const mins = match[2] ? Number.parseInt(match[2], 10) : 0;
  const total = hours * 60 + mins;
  return total > 0 ? total : undefined;
}

/**
 * Extended natural-language estimate parser.
 * Handles "45 mins", "1 hour 15", "1.5 hours", "half an hour", "2 hours 30 minutes" etc.
 * Falls back to parseEstimate for compact forms like "1h30m".
 */
export function parseEstimateNatural(input: string): number | undefined {
  if (!input) return undefined;
  const s = input.trim().toLowerCase().replace(/\s+/g, ' ');
  if (!s) return undefined;

  // Try compact parser first (handles 30m, 2h, 1h30m, bare integers)
  const compact = parseEstimate(s.replace(/\s/g, ''));
  if (compact !== undefined) return compact;

  // "half [an] hour"
  if (/half(?:\s+an?)?\s+hour/.test(s)) return 30;

  // "X hours Y mins" / "X hours Y" (bare number = mins) / "X hours"
  const hm = /^(?:(\d+(?:\.\d+)?)\s*hours?)?(?:\s*and\s*)?(?:\s*(\d+)\s*(?:mins?|minutes?))?$/.exec(
    s,
  );
  if (hm?.[1] !== undefined || hm?.[2] !== undefined) {
    const h = hm[1] !== undefined ? Number.parseFloat(hm[1]) : 0;
    const m = hm[2] !== undefined ? Number.parseInt(hm[2], 10) : 0;
    const total = Math.round(h * 60) + m;
    if (total > 0) return total;
  }

  // "X hours Y" — hours then bare number (minutes)
  const hb = /^(\d+)\s*hours?\s+(\d+)$/.exec(s);
  if (hb?.[1] && hb?.[2]) {
    const total = Number.parseInt(hb[1], 10) * 60 + Number.parseInt(hb[2], 10);
    if (total > 0) return total;
  }

  // "X mins?" only
  const m = /^(\d+)\s*(?:mins?|minutes?)$/.exec(s);
  if (m?.[1]) {
    const n = Number.parseInt(m[1], 10);
    if (n > 0) return n;
  }

  return undefined;
}
